import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

import emailValidator from '../../foundation/js/inputValidators/emailValidator'
import phoneValidator from '../../foundation/js/inputValidators/phoneValidator'
import nameValidator from '../../foundation/js/inputValidators/nameValidator'
import textAreaValidator from '../../foundation/js/inputValidators/textAreaValidator'
import companyNameValidator from '../../foundation/js/inputValidators/companyNameValidator'
import cvrValidator from '../../foundation/js/inputValidators/cvrValidator'
import eanValidator from '../../foundation/js/inputValidators/eanValidator'
import positiveNumberValidator from '../../foundation/js/inputValidators/positiveNumberValidator'

import FormInputTextField from 'components/FormInputTextField/FormInputTextField.vue'
import FormAddressField from 'components/FormAddressField/FormAddressField.vue'

import { checkoutScrollToTop } from '../CheckoutContent/CheckoutScrollToTop'

export default {
    name: "CheckoutStepOne",
    components: {FormInputTextField, FormAddressField},
    setup(){
        onMounted(() => checkoutScrollToTop())
        
        const store = useStore()
        const getLabel = (labelKey)=> store.getters['labels/getLabel']({labelCollection: 'checkout', labelKey: labelKey})
        const isSubmitting = computed(()=> store.state.checkout.isSubmitting)
        const checkoutSteps = computed( () => store.state.checkout.checkoutSteps)
        const currentStep = computed( () => checkoutSteps.value.find(i => i.stepNumber === 2))
        const beenFilled = computed(()=> currentStep.value.beenFilled)
        
        // use beenFilled to access whether step data is in sessionStorage, then make unSubmitted false only if it has
        const unSubmitted = ref(beenFilled.value ? false : true)
        const hideAllErrors = ref(false)
        
        const firstNameInput = ref(store.state.checkout.purchaseData.invoice.userInformation.contactFirstName)
        const firstNameInputFilled = computed (() => {
            return (unSubmitted.value && !firstNameInput.value) ? true : nameValidator(firstNameInput.value)
        })

        const lastNameInput = ref(store.state.checkout.purchaseData.invoice.userInformation.contactLastName)
        const lastNameInputFilled = computed (() => {
            return (unSubmitted.value && !lastNameInput.value) ? true : nameValidator(lastNameInput.value)
        })

        const phoneInput = ref(store.state.checkout.purchaseData.invoice.userInformation.mobilePhone)
        const phoneInputFilled = computed (() => {
            return (unSubmitted.value && !phoneInput.value) ? true : !!phoneValidator(phoneInput.value) 
        })

        const emailInput = ref(store.state.checkout.purchaseData.invoice.userInformation.email)
        const emailInputFilled = computed (() => {
            return (unSubmitted.value && !emailInput.value) ? true : !!emailValidator(emailInput.value) 
        })

        const addressLineInput = ref(store.state.checkout.purchaseData.invoice.invoiceAddress.addressLine)
        const addressLineInputFilled = computed (() => {
            return unSubmitted.value ? true : (addressLineInput.value.length > 0  && textAreaValidator(addressLineInput.value) && postalCodeInputFilled.value && cityInputFilled.value)
        })

        const postalCodeInput = ref(store.state.checkout.purchaseData.invoice.invoiceAddress.postalCode)
        const postalCodeInputFilled = computed (() => {
            return unSubmitted.value ? true : (postalCodeInput.value.length === 4  && textAreaValidator(addressLineInput.value))
        })

        const cityInput = ref(store.state.checkout.purchaseData.invoice.invoiceAddress.city)
        const cityInputFilled = computed (() => {
            return unSubmitted.value ? true : (cityInput.value.length > 0 && textAreaValidator(addressLineInput.value))
        })

        const customerTypeInput = ref(store.state.checkout.purchaseData.invoice.userInformation.customerType)

        const isCompany = computed(() => customerTypeInput.value === 'company')
        const isPublicInstitution = computed(() => customerTypeInput.value === 'publicInstitution')

        // if company
        const companyNameInput = ref(store.state.checkout.purchaseData.invoice.company.companyName)
        const companyNameInputFilled = computed (() => {
            return (unSubmitted.value && !companyNameInput.value) ? true : companyNameValidator(companyNameInput.value)
        })
        
        const companyDepartmentInput = ref(store.state.checkout.purchaseData.invoice.company.companyDepartment)
        const companyDepartmentInputFilled = computed (() => {
            return (unSubmitted.value && !companyDepartmentInput.value) ? true : ( companyDepartmentInput.value ? companyNameValidator(companyDepartmentInput.value) : true)
        })

        const companyCvrInput = ref(store.state.checkout.purchaseData.invoice.company.companyCvr)
        const companyCvrInputFilled = computed (() => {
            return (unSubmitted.value && !companyCvrInput.value) ? true : cvrValidator(companyCvrInput.value)
        })

        // if public institution
        const publicInstitutionNameInput = ref(store.state.checkout.purchaseData.invoice.public.publicInstitutionNameInput)
        const publicInstitutionNameInputFilled = computed (() => {
            return (unSubmitted.value && !publicInstitutionNameInput.value) ? true : companyNameValidator(publicInstitutionNameInput.value)
        })
        const publicInstitutionDepartmentOrContact = ref(store.state.checkout.purchaseData.invoice.public.publicInstitutionDepartmentOrContact)
        const publicInstitutionDepartmentOrContactFilled = computed (() => {
            return (unSubmitted.value && !publicInstitutionDepartmentOrContact.value)  ? true : (publicInstitutionDepartmentOrContact.value ? companyNameValidator(publicInstitutionDepartmentOrContact.value) : true)
        })
        const publicInstitutionUserId = ref(store.state.checkout.purchaseData.invoice.public.publicInstitutionUserId)
            // fx. 'Københavns Kommune'
        const publicInstitutionUserIdFilled = computed (() => {
            return (unSubmitted.value && !publicInstitutionUserId.value) ? true : (publicInstitutionUserId.value ? textAreaValidator(publicInstitutionUserId.value) : true)
        })
        const publicInstitutionEan = ref(store.state.checkout.purchaseData.invoice.public.publicInstitutionEan)
        const publicInstitutionEanFilled = computed (() => {
            return (unSubmitted.value && !publicInstitutionEan.value) ? true : eanValidator(publicInstitutionEan.value)
        })

        const inputIsValid = computed(function(){ 
            // only validating the required inputs    
            
            let addressFilled = addressLineInputFilled.value && 
            postalCodeInputFilled.value &&
            cityInputFilled.value

            let companyDetailsFilled = isCompany.value ? 
            (companyNameInputFilled.value &&
            companyCvrInputFilled.value) :
            true
            
            let publicInstitutionDetailsFilled = isPublicInstitution.value ? 
            (publicInstitutionNameInputFilled.value &&
            publicInstitutionEanFilled.value) :
            true
            
            return (
                firstNameInputFilled.value &&
                lastNameInputFilled.value &&
                phoneInputFilled.value &&
                emailInputFilled.value &&
                addressFilled &&
                companyDetailsFilled && 
                publicInstitutionDetailsFilled
            )
        })
        
        
        const onSubmit = function() {
            unSubmitted.value = false

            hideAllErrors.value = true
            setTimeout(()=>{
                // we delay a bit here to allow screenreaders to catchup to changes in the DOM about invalid fields.
                hideAllErrors.value = false
            },100)
            
            if (!inputIsValid.value) 
                return false            
            
            const checkoutInvoicePayload = {
                invoiceAddress: {
                    addressLine: addressLineInput.value,
                    postalCode: postalCodeInput.value,
                    city: cityInput.value,
                },
                userInformation: {
                    mobilePhone: phoneInput.value,
                    email: emailInput.value,
                    contactFirstName: firstNameInput.value,
                    contactLastName: lastNameInput.value,
                    customerType: customerTypeInput.value,
                },
                // company inputs
                company: {
                    companyName: companyNameInput.value,
                    companyDepartment: companyDepartmentInput.value,
                    companyCvr: companyCvrInput.value
                },
                // public institution inputs
                public: {
                    publicInstitutionNameInput: publicInstitutionNameInput.value, 
                    publicInstitutionDepartmentOrContact: publicInstitutionDepartmentOrContact.value, 
                    publicInstitutionUserId: publicInstitutionUserId.value,
                    publicInstitutionEan: publicInstitutionEan.value
                }
            }
            // set active delivery address as default to invoice address
            store.commit('checkout/set_active_delivery_address', checkoutInvoicePayload.invoiceAddress)
            
            store.commit('checkout/set_purchase_data_invoice', checkoutInvoicePayload)

            // if succes set been filled to current step, as data is now in session storage
            store.commit('checkout/update_checkout_step_been_filled', 1)

            // if succes set step to next stepNumber
            store.commit('checkout/update_checkout_step',{nextStepNumber: 2})
        }

        const showCompanyInputs = computed(() => isCompany.value)
        const showPublicInstitutionInputs = computed(() => isPublicInstitution.value)

        return {
            hideAllErrors,
            getLabel,
            firstNameInput,
            lastNameInput,
            phoneInput,
            emailInput,
            addressLineInput,
            postalCodeInput,
            cityInput,
            companyNameInput,
            companyDepartmentInput,
            companyCvrInput,
            publicInstitutionNameInput,
            publicInstitutionDepartmentOrContact,
            publicInstitutionUserId,
            publicInstitutionEan,
            onSubmit,
            isSubmitting,
            unSubmitted,
            customerTypeInput,
            isPublicInstitution,
            showPublicInstitutionInputs,
            isCompany,
            showCompanyInputs,
            // inputs filled
            companyNameInputFilled,
            companyDepartmentInputFilled,
            companyCvrInputFilled,
            publicInstitutionNameInputFilled,
            publicInstitutionDepartmentOrContactFilled,
            publicInstitutionUserIdFilled,
            publicInstitutionEanFilled,
            firstNameInputFilled,
            lastNameInputFilled,
            phoneInputFilled,
            emailInputFilled,
            addressLineInputFilled,
            postalCodeInputFilled,
            cityInputFilled
        }
        
    }
}